import { useState } from "react";
import MainLayout from "Layouts/MainLayout/MainLayout";
import { Button } from "Components/Common/Button/Button";
import { Toggle } from "Components/Common/ToggleBtn/Toggle";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { ApiError } from "Components/Common/ApiError";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import {
  useMailOrderPharmacyDefinitions,
  useResolveMailOrderPharmacyDefinition,
  useSetMailOrderPharmacyDefinitionActive,
} from "CustomHooks/useMailOrderPharmacies";
import { MailOrderPharmacyForm } from "./MailOrderPharmacyForm";

const statusStyle = {
  available: "bg-green-100 text-green-800",
  unavailable: "bg-red-100 text-red-800",
  ambiguous: "bg-yellow-100 text-yellow-800",
  pending: "bg-gray-100 text-gray-700",
};

const formatResolvedAt = (value) => value ? new Date(value).toLocaleString() : "Never";

export const MailOrderPharmacies = () => {
  const definitionsQuery = useMailOrderPharmacyDefinitions();
  const setActive = useSetMailOrderPharmacyDefinitionActive();
  const resolve = useResolveMailOrderPharmacyDefinition();
  const [editing, setEditing] = useState(undefined);
  const [formOpen, setFormOpen] = useState(false);
  const [apiError, setApiError] = useState("");
  const definitions = definitionsQuery.data || [];

  const openForm = (definition) => {
    setEditing(definition);
    setFormOpen(true);
  };

  const mutationError = (error) => {
    setApiError(errorFormatter(error?.response?.data?.errors || error?.message));
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Mail-order pharmacies</h1>
            <p className="text-sm text-gray-500 max-w-3xl">
              Manage the curated pharmacies that bypass patient-state matching. DoseSpot queries resolve the current pharmacy ID; only definitions with one verified match can be activated.
            </p>
          </div>
          <Button btnText="Add pharmacy" className="h-10 px-4 flex-shrink-0" onClick={() => openForm(null)} />
        </div>

        {(apiError || definitionsQuery.error) && (
          <ApiError
            error={apiError || errorFormatter(definitionsQuery.error?.response?.data?.errors || definitionsQuery.error?.message)}
            setApiError={setApiError}
          />
        )}

        {definitionsQuery.isLoading ? <Spinner /> : (
          <div className="align-middle inline-block min-w-full shadow border-b border-gray-200 sm:rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {["Pharmacy", "Queries", "Last DoseSpot match", "Status", "Active", ""].map((heading) => (
                    <th key={heading} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {definitions.map((definition) => {
                  const status = definition.lastResolution?.status || "pending";
                  const resolving = resolve.isLoading && resolve.variables === definition.id;
                  const toggling = setActive.isLoading && setActive.variables?.id === definition.id;
                  return (
                    <tr key={definition.id}>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">{definition.displayName}</div>
                        <div className="text-xs text-gray-500">{definition.provider}</div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {(definition.queries || []).map((query, index) => (
                          <div key={index}>{Object.entries(query).map(([key, value]) => `${key}: ${value}`).join(", ")}</div>
                        ))}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        <div>{definition.lastResolution?.storeName || "—"}</div>
                        {definition.lastResolution?.pharmacyId && <div className="text-xs text-gray-500">ID {definition.lastResolution.pharmacyId} · NCPDP {definition.lastResolution.ncpdpId || "—"}</div>}
                        <div className="text-xs text-gray-400">{formatResolvedAt(definition.lastResolution?.resolvedAt)}</div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyle[status] || statusStyle.pending}`}>{status}</span>
                      </td>
                      <td className="px-4 py-4">
                        <Toggle
                          enabled={!!definition.active}
                          disabled={toggling}
                          onChange={() => setActive.mutate(
                            { id: definition.id, active: !definition.active },
                            { onError: mutationError },
                          )}
                        />
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            btnText="Resolve"
                            variant="Secondary"
                            className="h-8 px-2"
                            isLoading={resolving}
                            isDisabled={resolving}
                            onClick={() => resolve.mutate(definition.id, { onError: mutationError })}
                          />
                          <Button btnText="Edit" variant="Secondary" className="h-8 px-2" onClick={() => openForm(definition)} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!definitions.length && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">No mail-order pharmacy definitions found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formOpen && <MailOrderPharmacyForm definition={editing} onClose={() => { setFormOpen(false); setEditing(undefined); }} />}
    </MainLayout>
  );
};

